import { useRef, useState } from 'react';
import type { LayoutChangeEvent, View } from 'react-native';
import { Modal, Pressable } from 'react-native';
import type { MeasuredDimensions } from 'react-native-reanimated';
import Animated, {
  useAnimatedStyle,
  useSharedValue,
} from 'react-native-reanimated';

import { Box, Button, HStack, Text, VStack, theme } from '@squire/ui';

import { CheckIcon, InfoCircleIcon } from '@/components/_icons';
import { ServiceCardGift } from '@/components/booking/ServiceCardGift';
import { useScreenTranslations } from '@/hooks/useScreenTranslations';
import { pressedState } from '@/utils/a11y';
import { formatPrice } from '@/utils/internationalizationUtils';
import { resolveLocalizedText } from '@/utils/localizedText';

import { styles } from './styles';
import type { ServiceCardProps, ServiceCardServiceProps } from './types';

const AnimatedPressable = Animated.createAnimatedComponent(Pressable);

function ServiceCardService({
  service,
  selected,
  onPress,
  onInfoPress,
  showDescription: externalShowDescription,
  testID,
  style,
  currency = 'USD',
  ...props
}: ServiceCardServiceProps) {
  const { t, i18n } = useScreenTranslations('ServiceCard');
  const [internalShowDescription, setInternalShowDescription] = useState(false);
  const [priceHeight, setPriceHeight] = useState(0);

  const containerRef = useRef<View>(null);

  const cardRect = useSharedValue<MeasuredDimensions | null>(null);

  const showDescription = externalShowDescription ?? internalShowDescription;

  styles.useVariants({ state: selected });

  const handleInfoPress = () => {
    containerRef.current?.measure((x, y, width, height, pageX, pageY) => {
      cardRect.value = { x, y, width, height, pageX, pageY };

      if (onInfoPress) {
        onInfoPress();
      } else {
        setInternalShowDescription((prev) => !prev);
      }
    });
  };

  const handleCloseDescription = () => {
    setInternalShowDescription(false);
  };

  const formatDuration = (minutes: number): string => {
    if (minutes < 60) {
      return `${minutes} ${t('duration.minutes')}`;
    }
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    if (remainingMinutes === 0) {
      return `${hours}${t('duration.hours')}`;
    }
    return `${hours}${t('duration.hours')} ${remainingMinutes}${t('duration.minutesShort')}`;
  };

  const handleCardPress = () => {
    onPress?.(cardRect.value);
  };

  const description =
    resolveLocalizedText(service.localizedDesc, i18n.language) || service.desc;
  const hasDescription = !!description;

  const { costRange, durationRange } = service;

  const hasPriceRange =
    costRange !== undefined && costRange.min !== costRange.max;

  const formattedPrice = hasPriceRange
    ? `${formatPrice(costRange.min / 100, { locale: i18n.language, currency })} - ${formatPrice(costRange.max / 100, { locale: i18n.language, currency })}`
    : formatPrice(service.cost / 100, { locale: i18n.language, currency });

  const hasDurationRange =
    durationRange !== undefined && durationRange.min !== durationRange.max;

  const durationText = hasDurationRange
    ? `${formatDuration(durationRange.min)} - ${formatDuration(durationRange.max)}`
    : formatDuration(service.duration);

  const handlePriceLayout = (event: LayoutChangeEvent) => {
    const { height } = event.nativeEvent.layout;

    setPriceHeight((prev) => (prev === height ? prev : height));
  };

  const handleDescriptionExpandedLayout = (event: LayoutChangeEvent) => {
    cardRect.value = {
      ...event.nativeEvent.layout,
      pageX: event.nativeEvent.layout.x,
      pageY: event.nativeEvent.layout.y,
    };
  };

  const renderContent = (_showDescription: boolean) => (
    <>
      <HStack style={styles.header}>
        <VStack style={styles.serviceInfo} space={theme.spacing[2]}>
          <Text
            variant="mStrong"
            color={selected === 'main' ? 'textOnSelected' : 'textPrimary'}
            numberOfLines={_showDescription ? undefined : 4}
          >
            {service.name}
          </Text>
          <Text
            variant="s"
            color={
              selected === 'main'
                ? 'textOnSelected'
                : selected
                  ? 'textPrimary'
                  : 'textSecondary'
            }
            style={styles.duration}
          >
            {durationText}
          </Text>
        </VStack>

        {selected !== 'main' && hasDescription && (
          <Button
            testID="service-card-info-button"
            variant="secondary"
            size="small"
            onPress={handleInfoPress}
            style={styles.infoButton}
            accessibilityLabel={t('accessibility.serviceInformation')}
            accessibilityHint={
              _showDescription
                ? t('accessibility.hideDescription')
                : t('accessibility.showDescription')
            }
          >
            <InfoCircleIcon
              size={16}
              color={_showDescription ? 'borderBrand' : 'borderPrimary'}
            />
          </Button>
        )}

        {selected === 'main' && (
          <Box style={styles.selectedIconContainer}>
            <CheckIcon size={12} color="textPrimaryInverse" />
          </Box>
        )}
      </HStack>

      {showDescription && (
        <VStack testID="service-card-description" style={styles.description}>
          <Text variant="s" color="textSecondary">
            {description}
          </Text>
          <Box
            testID="service-card-price-spacer"
            style={styles.priceSpacer(priceHeight)}
            pointerEvents="none"
          />
        </VStack>
      )}

      <Box
        testID="service-card-price"
        style={styles.price}
        onLayout={handlePriceLayout}
      >
        <Text
          variant="sStrong"
          color={selected === 'main' ? 'textOnSelected' : 'textPrimary'}
        >
          {formattedPrice}
        </Text>
      </Box>
    </>
  );

  const rExpandedDescriptionStyle = useAnimatedStyle(() => {
    return {
      top: cardRect.value?.pageY,
      left: cardRect.value?.pageX,
      width: cardRect.value?.width,
      minHeight: cardRect.value?.height,
    };
  });

  const showExpandedCard =
    externalShowDescription || (hasDescription && showDescription);

  return (
    <>
      <AnimatedPressable
        {...props}
        ref={containerRef}
        onPress={handleCardPress}
        style={[styles.container]}
        testID={testID}
        accessibilityRole="button"
        accessibilityLabel={t('accessibility.serviceDetails', {
          name: service.name,
          duration: durationText,
          price: formattedPrice,
        })}
        accessibilityHint={
          selected
            ? t('accessibility.currentlySelected')
            : t('accessibility.tapToSelect')
        }
        {...pressedState(!!selected)}
        onLayout={handleDescriptionExpandedLayout}
      >
        {({ pressed }) => (
          <Box style={[styles.content({ pressed }), style]}>
            {!showExpandedCard && renderContent(false)}
          </Box>
        )}
      </AnimatedPressable>

      <Modal
        testID="service-card-modal"
        transparent
        animationType="none"
        visible={showExpandedCard}
      >
        <Pressable
          testID="service-card-modal-overlay"
          onPress={handleCloseDescription}
          style={styles.modalOverlay}
        />
        <Animated.View
          style={[
            styles.container,
            styles.content({ pressed: false }),
            styles.withDescription,
            rExpandedDescriptionStyle,
          ]}
          onLayout={handleDescriptionExpandedLayout}
          testID="service-card-modal-content"
        >
          {renderContent(true)}
        </Animated.View>
      </Modal>
    </>
  );
}

function ServiceCard(props: ServiceCardProps) {
  if (props.mode === 'giftCard') {
    return <ServiceCardGift {...props} />;
  }
  return <ServiceCardService {...props} />;
}

ServiceCard.displayName = 'ServiceCard';
export { ServiceCard };
